// One way in to everything the interface asks about the economy.
//
// The readouts, the inspector, the badges and the overlays each derive what a
// square is doing — what it needs, what it is processing, what it can afford —
// and they were each deriving it separately, on every render. `PieceOverlays`
// built a badge for *every* piece on the board and the detail surface then
// built the selected piece's badge again a few components away, with nothing
// between them to notice they were asking the same question.
//
// So there is one entry point now, and it remembers.
//
// **Keyed on the state object, which is a sound key here and is not
// everywhere.** A `GameState` published by the store is immutable: `cloneState`
// mints a new object per command and nothing writes to one after it is
// published, so identity means "the same board" *exactly*. A `Position` is the
// opposite — make/unmake mutate one in place — which is why `NearCache` in
// `src/engine/ai/` is built per call and thrown away rather than hung off a position.
// Do not copy this pattern onto a position.
//
// **A ring of the last few, not a single `useMemo`.** One remembered answer is
// enough for a component that re-renders against an unchanged board, and it is
// not enough for the board this actually is: the renderer runs a beat behind
// the reducer, undo is truncate-and-replay, and two components can commit
// against states one command apart. Any of those makes a one-deep memo
// alternate between two keys and hit neither — the classic thrash, and it looks
// exactly like no cache at all. A handful of entries makes going back and forth
// free.
//
// **Module state rather than a context**, and that is a decision worth stating:
// every consumer already holds the state, so a provider would buy nothing but a
// re-render dependency and a component to keep in the tree. Sharing here is by
// *identity of the data*, which needs no plumbing and works between two
// components that never meet.

import type { Color } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { type PieceBadge, pieceBadges, pieceDetail } from "./pieceBadges";
import { type ShelfTotals, shelfTotals } from "src/engine/session/shelfTotals";

/**
 * How many boards are remembered.
 *
 * Five. Enough to cover the gaps that make a one-deep memo thrash — a
 * renderer a beat behind, an undo, two components a command apart — and small
 * enough that what is retained is bounded and obvious. These are strong
 * references to whole game states, so this is a number to keep honest rather
 * than raise idly; the store is holding the log anyway, but a hundred of these
 * would be a leak wearing a cache's clothes.
 */
export const ECONOMY_DEPTH = 5;

/**
 * Everything a component may ask about the economy of one board.
 *
 * Each field is computed **at most once per board**, and lazily: a shelf that
 * never opens the watching view never pays for a second army's totals, and a
 * board nobody has selected a piece on never derives a detail. The badge sweep
 * is the one thing taken up front, because the overlays always want all of it
 * and the only consumer that does not is asking for one entry of the same set.
 */
export interface Economy {
  /** Every piece with something to say. The overlays draw these. */
  badges: PieceBadge[];
  /** The same, by square, so a lookup is not a scan. */
  bySquare: Map<number, PieceBadge>;
  /**
   * One square's detail, which is **never** `bySquare.get(sq)`.
   *
   * `pieceBadges` filters — a plain pawn with nothing to report is left out —
   * while the inspector has to say something about whatever you clicked. It
   * also says *less*: an overlay badge is what the board draws over a piece,
   * and `pieceDetail` is that plus the climb (`upgrades`), which hangs off the
   * detail alone. Handing back the overlay's entry as the fast path lost the
   * Climb section on every foundry, refinery and miner — the pieces most worth
   * specialising — while the pure model's test passed on a rook nobody drew.
   * So a detail is always derived, once per square per state, and the miss is
   * remembered too: clicking the same quiet pawn twice asks once.
   */
  detail(square: number): PieceBadge | null;
  /** What one army has, is moving, and has not dug yet. */
  totals(side: Color): ShelfTotals;
}

interface Entry {
  state: GameState;
  economy: Economy;
}

/**
 * The ring. Newest first, so the common case — the board that is on screen —
 * is the first thing compared.
 */
let ring: Entry[] = [];

function build(state: GameState): Economy {
  const badges = pieceBadges(state);
  const bySquare = new Map(badges.map((badge) => [badge.square, badge]));
  // Lazily filled, and holding `null` as a real answer: a square with nothing
  // to say is a result worth remembering, not a miss to re-derive.
  const details = new Map<number, PieceBadge | null>();
  const armies = new Map<Color, ShelfTotals>();

  return {
    badges,
    bySquare,
    detail(square) {
      if (details.has(square)) return details.get(square) ?? null;
      const fresh = pieceDetail(state, square);
      details.set(square, fresh);
      return fresh;
    },
    totals(side) {
      const held = armies.get(side);
      if (held) return held;
      const fresh = shelfTotals(state, side);
      armies.set(side, fresh);
      return fresh;
    }
  };
}

/**
 * The economy of this board, computed once and remembered.
 *
 * Pure in the state, which is what makes it safe to call from anywhere: two
 * components asking about the same board get the *same object*, so the work
 * happens once however many of them ask.
 */
export function economyFor(state: GameState): Economy {
  const at = ring.findIndex((entry) => entry.state === state);
  if (at >= 0) {
    const [hit] = ring.splice(at, 1);
    // Touched, so the ring is least-recently-used rather than merely the last
    // five *built* — otherwise a board being asked about repeatedly could be
    // evicted by four boards nobody looks at again.
    ring.unshift(hit);
    return hit.economy;
  }
  const economy = build(state);
  ring.unshift({ state, economy });
  // Trimmed rather than shifted one at a time: the ring only ever grows by one
  // here, but a changed `ECONOMY_DEPTH` should take effect immediately rather
  // than one eviction at a time.
  if (ring.length > ECONOMY_DEPTH) ring = ring.slice(0, ECONOMY_DEPTH);
  return economy;
}

/**
 * Forget everything. For tests, and for a new game.
 *
 * Not called on an ordinary command: the ring evicts by itself, and clearing on
 * every state change would be a cache that throws away exactly what it is for.
 */
export function clearEconomy(): void {
  ring = [];
}

/**
 * The component-level entry point.
 *
 * A plain function rather than a hook wrapping `useMemo`, and deliberately: the
 * ring already remembers across renders *and across components*, which is more
 * than a `useMemo` in any one of them can do. Named `useEconomy` because that
 * is what it is for and where it is called.
 */
export const useEconomy = (state: GameState): Economy => economyFor(state);
